// ─────────────────────────────────────────────────────────
// Einstellungen → Mein Konto → Aktive Geräte
// ─────────────────────────────────────────────────────────
// Zeigt, wo dieses Konto gerade angemeldet ist, und lässt jedes Gerät einzeln
// abmelden oder umbenennen. Wie «Passwort ändern» gehört der Bereich dem
// NUTZER, nicht der Firma — darum KEIN Permission-Gate: die eigenen Geräte
// darf jeder sehen. Die Grenze zieht der Server über die userId aus dem Token,
// nicht eine Berechtigung.
//
// Eigene Datei statt ein weiterer Block in settingsAccount.js: dort liegen
// bereits Passwort UND Zwei-Faktor, und beide wachsen noch. Prefix `dv-`.
//
// WARUM DAS ÜBERHAUPT EXISTIERT: «ein Konto, ein Gerät» ist eine Regel, die
// Leute aussperrt. Wer nicht sehen kann, WO er angemeldet ist, kann auch nicht
// verstehen, warum er gerade rausgeflogen ist. Diese Liste ist die Antwort auf
// «wer benutzt mein Konto?» — und damit auch ein Sicherheitswerkzeug.

import { icon, escapeHtml } from '../../utils/dom.js';
import { fetchDevices, signOutDevice, renameDevice } from '../../core/auth.js';
import { t } from '../../i18n/index.js';

/** «vor 5 Minuten» — Intl spricht alle 17 Sprachen von selbst. */
function relativeTime(iso) {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diffSec = Math.round((then - Date.now()) / 1000);
  const rtf = new Intl.RelativeTimeFormat(document.documentElement.lang || 'de', { numeric: 'auto' });
  const abs = Math.abs(diffSec);
  if (abs < 60) return rtf.format(Math.round(diffSec), 'second');
  if (abs < 3600) return rtf.format(Math.round(diffSec / 60), 'minute');
  if (abs < 86400) return rtf.format(Math.round(diffSec / 3600), 'hour');
  return rtf.format(Math.round(diffSec / 86400), 'day');
}

function deviceRow(d, busyId) {
  const busy = busyId === d.id;
  return `
    <li class="dv-item${d.current ? ' dv-item--current' : ''}">
      <span class="dv-ico">${icon('devices', 20)}</span>
      <span class="dv-text">
        <strong>${escapeHtml(d.label || t('auth.device.unknown'))}</strong>
        <small>${escapeHtml(relativeTime(d.lastSeenAt))}${d.ipAddress ? ' · ' + escapeHtml(d.ipAddress) : ''}</small>
      </span>
      ${d.current
        // Das eigene Gerät hier abzumelden wäre ein Eigentor: der Nutzer flöge
        // aus der Seite, auf der er gerade steht. Dafür gibt es «Abmelden».
        ? `<span class="dv-badge">${escapeHtml(t('settings.devices.thisDevice'))}</span>`
        : `<span class="dv-actions">
             <button class="cd-btn-ghost dv-btn" data-action="dv-rename" data-id="${escapeHtml(d.id)}" ${busy ? 'disabled' : ''}>
               ${escapeHtml(t('settings.devices.rename'))}
             </button>
             <button class="cd-btn-ghost dv-btn dv-btn--danger" data-action="dv-signout" data-id="${escapeHtml(d.id)}" ${busy ? 'disabled' : ''}>
               ${escapeHtml(busy ? t('common.saving') : t('settings.devices.signOut'))}
             </button>
           </span>`}
    </li>`;
}

export function devicesCard(ctx) {
  const { s } = ctx;
  const head = `<div class="cd-emp-card-head">${icon('devices', 18)}<h3>${escapeHtml(t('settings.devices.title'))}</h3></div>`;

  if (s.dvError) {
    return `<div class="cd-emp-card">${head}
      <div class="cd-error">${icon('error', 18)}<span>${escapeHtml(s.dvError)}</span></div>
      <button class="cd-btn-ghost" data-action="dv-reload">${escapeHtml(t('common.retry'))}</button>
    </div>`;
  }

  // `undefined` = noch nie geladen (loadDevices läuft gerade), `[]` = wirklich
  // keine Geräte. Der Unterschied muss sichtbar bleiben, sonst blinkt «keine
  // Geräte» auf, bevor die Antwort da ist.
  if (!s.dvDevices) {
    return `<div class="cd-emp-card">${head}<p class="cd-sc-lead">${escapeHtml(t('common.loading'))}</p></div>`;
  }

  // Der Satz «dieses Konto läuft nur auf einem Gerät» stimmt nur, wenn das
  // Limit auch 1 ist — bei einem Demo-Konto mit 5 wäre er schlicht falsch.
  const limitNote = s.dvMax === 1
    ? t('settings.devices.limitOne')
    : t('settings.devices.limitN').replace('{n}', String(s.dvMax ?? 1));

  return `
    <div class="cd-emp-card">
      ${head}
      <p class="cd-sc-lead">${escapeHtml(t('settings.devices.lead'))}</p>
      <ul class="dv-list">${s.dvDevices.map((d) => deviceRow(d, s.dvBusyId)).join('')}</ul>
      <div class="cd-emp-info cd-sc-note">${icon('info', 18)}<span>${escapeHtml(limitNote)}</span></div>
    </div>`;
}

/** Lädt die Liste. Bewusst ohne await im Aufrufer — die Karte zeichnet sich neu. */
export function loadDevices(ctx) {
  const { s, draw } = ctx;
  if (s.dvLoading) return;
  s.dvLoading = true;
  s.dvError = '';
  fetchDevices()
    .then((r) => { s.dvDevices = r.devices || []; s.dvMax = r.maxSessions ?? 1; })
    .catch((e) => { s.dvError = e?.message || t('settings.devices.loadFailed'); })
    .finally(() => { s.dvLoading = false; draw(); });
}

export function handleDevicesAction(a, el, ctx) {
  const { s, draw } = ctx;

  if (a === 'dv-reload') {
    s.dvDevices = null;
    loadDevices(ctx);
    draw();
    return true;
  }

  if (a === 'dv-signout') {
    const id = el.dataset.id;
    if (!id) return true;
    // Rückfrage, weil es nicht rückgängig zu machen ist: das andere Gerät
    // muss danach das Passwort neu eingeben.
    if (!window.confirm(t('settings.devices.signOutConfirm'))) return true;
    s.dvBusyId = id;
    s.dvError = '';
    draw();
    signOutDevice(id)
      .then(() => {
        // Lokal entfernen statt neu laden: die Liste ist kurz, und ein
        // zweiter Rundgang zum Server würde die Zeile nur später verschwinden
        // lassen.
        s.dvDevices = (s.dvDevices || []).filter((d) => d.id !== id);
      })
      .catch((e) => { s.dvError = e?.message || t('settings.devices.signOutFailed'); })
      .finally(() => { s.dvBusyId = null; draw(); });
    return true;
  }

  if (a === 'dv-rename') {
    const id = el.dataset.id;
    const current = (s.dvDevices || []).find((d) => d.id === id);
    if (!id || !current) return true;
    // prompt() statt eigenem Dialog: EIN Textfeld, kein Zustand, keine
    // Validierung ausser der Länge. Ein Modal dafür wäre mehr Gerüst als Inhalt.
    const next = window.prompt(t('settings.devices.renamePrompt'), current.deviceName || '');
    if (next === null) return true; // abgebrochen
    s.dvBusyId = id;
    s.dvError = '';
    draw();
    renameDevice(id, next.trim().slice(0, 60))
      .then(() => {
        const trimmed = next.trim().slice(0, 60);
        current.deviceName = trimmed || null;
        // Leerer Name = zurück zur automatischen Beschreibung. Die kennt nur
        // der Server (sie kommt aus dem User-Agent), deshalb hier neu laden.
        if (!trimmed) { s.dvDevices = null; loadDevices(ctx); return; }
        current.label = trimmed;
      })
      .catch((e) => { s.dvError = e?.message || t('settings.devices.renameFailed'); })
      .finally(() => { s.dvBusyId = null; draw(); });
    return true;
  }

  return false;
}
